import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/contact-us.css';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';

function ContactUs() {
  const [searchParams] = useSearchParams();
  const leadSource = searchParams.get('source') || 'contact_page';
  const leadInterest = searchParams.get('interestedIn') || 'general';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    consent: false
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ type: 'info', message: 'Sending...' });

    try {
      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: formData.phone,
          consent: formData.consent,
          company: formData.company,
          message: formData.message,
          source: leadSource,
          interestedIn: leadInterest,
        })
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Thank you for contacting us. We will get back to you soon!' });
        setFormData({ name: '', email: '', phone: '', company: '', message: '', consent: false });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again later.' });
    }
  };

  return (
    <div className="contact-page">
      <SEO 
        title="Contact Us | Lead With Tech - Get in Touch"
        description="Contact Lead With Tech for training inquiries, program information, or general questions. We're here to help you advance your tech career."
      />
      
      <section className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Have questions? We're here to help.</p>
        </div>
      </section>

      <section className="contact-content">
        <div className="container contact-grid">
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <div className="info-items">
              <div className="info-item">
                <h3>Email Us</h3>
                <a href="mailto:info@leadwithtech.in">info@leadwithtech.in</a>
              </div>
              
              <div className="info-item">
                <h3>Business Hours</h3>
                <p>Monday - Friday: 9:00 AM - 6:00 PM IST</p>
                <p>Saturday: 9:00 AM - 1:00 PM IST</p>
              </div>

              <div className="info-item">
                <h3>Connect With Us</h3>
                <div className="social-links">
                  <a href="https://linkedin.com/company/leadwithtech" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                    <i className="fab fa-linkedin"></i>
                  </a>
                  <a href="https://twitter.com/leadwithtech" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                    <i className="fab fa-twitter"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <form onSubmit={handleSubmit} className="contact-form">
              <h2>Send us a Message</h2>
              
              {submitStatus.message && (
                <div className={`form-status ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}

              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                />
              </div>

              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  required
                />
              </div>

              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  required
                />
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Company Name"
                />
              </div>

              <div className="form-group">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  rows="5"
                  required
                ></textarea>
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', textAlign: 'left' }}>
                <input
                  type="checkbox"
                  name="consent"
                  id="contact-consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  required
                  style={{ marginTop: '4px', width: 'auto', minWidth: '16px', flexShrink: 0 }}
                />
                <label htmlFor="contact-consent" style={{ fontSize: '0.9rem', lineHeight: '1.4', cursor: 'pointer' }}>
                  I allow to send me regular updates
                </label>
              </div>

              <button type="submit" className="submit-button">
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactUs;